import { createApp } from "vue";
import { localizeLegacyDom, translate } from "@/i18n";
import App from "./App.vue";
import store from "./store";
import router from "./router";

import "./assets/css/nucleo-icons.css";
import "./assets/css/nucleo-svg.css";

import MaterialDashboard from "./material-dashboard";

import PrimeVue from 'primevue/config';
import Aura from '@primevue/themes/aura';
import ToastService from 'primevue/toastservice';
import Multiselect from 'vue-multiselect';

import 'primeicons/primeicons.css';

// PrimeVue Components
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Toolbar from 'primevue/toolbar';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import Rating from 'primevue/rating';
import Tag from 'primevue/tag';
import FileUpload from 'primevue/fileupload';

import { definePreset } from '@primevue/themes';

const MyPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{yellow.50}',
            100: '{yellow.100}',
            200: '{yellow.200}',
            300: '{yellow.300}',
            400: '{yellow.400}',
            500: '{yellow.500}',
            600: '{yellow.600}',
            700: '{yellow.700}',
            800: '{yellow.800}',
            900: '{yellow.900}',
            950: '{yellow.950}'
        }
    }
});

// Load user from localStorage if exists
const storedUser = localStorage.getItem("user_free");
if (storedUser) {
    const userData = JSON.parse(storedUser);
    store.commit("auth/SET_USER", userData);
}

// Auth Guard
router.beforeEach((to, from, next) => {
    const requiresAuth = to.meta?.requiresAuth ?? false;
    const isLoggedIn = store.getters["auth/isLoggedIn"];

    if (requiresAuth && !isLoggedIn) {
        return next({ name: "Login" });
    }

    // Optional: prevent logged-in users from seeing login page
    if (to.name === "Login" && isLoggedIn && from.name) {
        next({ name: "Dashboard" });
    }

    return next();
});

// Create App
const appInstance = createApp(App);

appInstance.config.globalProperties.$t = function translateForCurrentLocale(key, replacements = {}) {
  return translate(this.$store.state.locale, key, replacements);
};

// Register Global Components
appInstance.component('DataTable', DataTable);
appInstance.component('Column', Column);
appInstance.component('Toolbar', Toolbar);
appInstance.component('Button', Button);
appInstance.component('InputText', InputText);
appInstance.component('IconField', IconField);
appInstance.component('InputIcon', InputIcon);
appInstance.component('Rating', Rating);
appInstance.component('Tag', Tag);
appInstance.component('FileUpload', FileUpload);
appInstance.component('Multiselect', Multiselect);

// Register Plugins
appInstance.use(store);
appInstance.use(router);
appInstance.use(ToastService);
appInstance.use(PrimeVue, {
    theme: {
        preset: MyPreset,
    }
});
appInstance.use(MaterialDashboard);

// Mount after router is ready
router.isReady().then(() => {
    appInstance.mount("#app");
    const localize = () => requestAnimationFrame(() => localizeLegacyDom(store.state.locale));
    store.watch((state) => state.locale, localize, { immediate: true });
    new MutationObserver(localize).observe(document.body, { childList: true, subtree: true });
});
